import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AutomationsEmployeesService,
  EmployeeCreateBodyParams,
  TCEmployeeCreateResponse,
  EmployeeUpdateBodyParams,
  TCEmployeeUpdateResponse,
} from 'src/api/generated';

interface UseCreateEmployeeMutationResponse {
  createEmployee: (
    data: EmployeeCreateBodyParams
  ) => Promise<TCEmployeeCreateResponse>;
  loading: boolean;
  error: Error | null;
}

export const useCreateEmployeeMutation = (
  onSuccess: (newEmployee: TCEmployeeCreateResponse) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseCreateEmployeeMutationResponse => {
  const queryClient = useQueryClient();

  const createEmployee = async (
    data: EmployeeCreateBodyParams
  ): Promise<TCEmployeeCreateResponse> => {
    return await AutomationsEmployeesService.create(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: createEmployee,
    onSuccess: async (newEmployee) => {
      await queryClient.invalidateQueries({
        queryKey: ['automations/employees'],
      });

      onSuccess(newEmployee);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    createEmployee: mutateAsync,
    loading,
    error,
  };
};

interface UpdateEmployeeParams {
  employeeId: string;
  data: EmployeeUpdateBodyParams;
}

interface UseUpdateEmployeeMutationResponse {
  updateEmployee: (
    params: UpdateEmployeeParams
  ) => Promise<TCEmployeeUpdateResponse>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateEmployeeMutation = (
  onSuccess: (updatedEmployee: TCEmployeeUpdateResponse) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseUpdateEmployeeMutationResponse => {
  const queryClient = useQueryClient();

  const updateEmployee = async (
    params: UpdateEmployeeParams
  ): Promise<TCEmployeeUpdateResponse> => {
    return await AutomationsEmployeesService.update(
      params.employeeId,
      params.data
    );
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: updateEmployee,
    onSuccess: async (updatedEmployee) => {
      const employeeId = updatedEmployee.metadata.employeeId;

      await queryClient.invalidateQueries({
        queryKey: ['automations/employees'],
        exact: false,
      });

      await queryClient.invalidateQueries({
        queryKey: ['automations/employees/versions', employeeId],
        exact: false,
      });

      onSuccess(updatedEmployee);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    updateEmployee: mutateAsync,
    loading,
    error,
  };
};
